import { strFromU8 } from "fflate";
import type {
  BrandSpec,
  BrandIssue,
  BrandIssueCategory,
  BrandCheckOptions,
  BrandCheckResult,
} from "../../../types";
import { normalizeHex, colorDistance } from "../../../utils/colourUtils";
import { unzipPptxBase64, type PptxZip } from "../../apply-brand/services/brandApplier";
import { resolveRelationshipTarget } from "../../../utils/ooxmlRelationships";
import { extractNotesText } from "../../SpeakerNotes/services/speakerNotesOffice";
import { logError, logOfficeError, scrubMessage } from "../../../../../telemetry";

const COLOR_DISTANCE_THRESHOLD = 22;

// Position tolerance (points) before a shape is considered off-slide — absorbs
// float rounding noise from the Office.js position/size properties.
const OFF_SLIDE_TOLERANCE_PT = 1;

const SLIDE_PART_DIR = "ppt/slides";
const SLIDE1_XML_PATH = `${SLIDE_PART_DIR}/slide1.xml`;
const SLIDE1_RELS_PATH = `${SLIDE_PART_DIR}/_rels/slide1.xml.rels`;
const isCommentsRelationship = (type: string): boolean => type.toLowerCase().endsWith("/comments");

// Shape types that never carry a text frame (skip textFrame loading for these).
const NO_TEXTFRAME_TYPES = new Set([
  "image", "Image", "picture", "Picture", "linkedImage", "LinkedImage",
  "line", "Line", "connector", "Connector",
  "contentApp", "ContentApp", "media", "Media",
  "model3D", "Model3D", "ole", "Ole", "diagram", "Diagram",
  "smartArt", "SmartArt", "graphic", "Graphic", "ink", "Ink",
  "unsupported", "Unsupported",
]);

// Shape types that have no meaningful fill API. Tables and charts are included:
// loading their shape-level fill throws — their fills are checked via OOXML.
const NO_FILL_TYPES = new Set([
  "line", "Line", "connector", "Connector",
  "contentApp", "ContentApp", "unsupported", "Unsupported",
  "media", "Media", "model3D", "Model3D", "ole", "Ole",
  "diagram", "Diagram", "smartArt", "SmartArt", "graphic", "Graphic", "ink", "Ink",
  "table", "Table", "chart", "Chart",
]);

// Shape types that carry text but NOT via textFrame — loading textFrame on
// these rejects the whole sync batch. Their text is checked via OOXML.
const SPECIAL_TEXT_TYPES = new Set(["table", "Table", "chart", "Chart"]);

// Group shapes — contain child shapes which are scanned recursively.
const GROUP_TYPES = new Set(["group", "Group"]);

// Shape types where lineFormat loading is unsafe / meaningless.
const NO_LINE_TYPES = new Set([
  "table", "Table", "chart", "Chart", "group", "Group",
  "diagram", "Diagram", "smartArt", "SmartArt", "graphic", "Graphic",
  "contentApp", "ContentApp", "unsupported", "Unsupported",
]);

// Glyph/bullet fonts that must never be flagged as off-brand body fonts.
const SYMBOL_FONTS = new Set([
  "wingdings", "wingdings 2", "wingdings 3", "webdings", "symbol",
  "marlett", "mt extra", "segoe mdl2 assets", "segoe fluent icons",
]);

const MAX_GROUP_DEPTH = 5;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

/** Slide canvas size in points, or null when PageSetup (PowerPointApi 1.10) isn't available in this host. */
async function resolveSlideSize(
  context: PowerPoint.RequestContext,
): Promise<{ width: number; height: number } | null> {
  try {
    const pageSetup = context.presentation.pageSetup;
    pageSetup.load("slideWidth,slideHeight");
    await context.sync();
    return { width: pageSetup.slideWidth, height: pageSetup.slideHeight };
  } catch (err) {
    // Off-slide check is a nice-to-have, not a scan-blocking failure — but
    // this fires once per scan (not per shape), so it's worth tracing.
    logOfficeError(err, "brand-check-page-setup");
    return null;
  }
}

function errorMessage(e: unknown, fallback?: string): string {
  if (e instanceof Error) return e.message;
  return fallback ?? String(e);
}

/** connectorSystemName tag for all Deckorate brand-check telemetry — mirrors brandApplier's convention. */
const BRAND_CHECK_CONNECTOR = "deckorate";

/** Report a whole-scan brand-check failure through the shared telemetry pipeline (PII-scrubbed). */
function reportBrandCheckError(reason: string, type: string, message: string): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: BRAND_CHECK_CONNECTOR, reason },
  );
}

/** Read a zip entry's contents as a UTF-8 string, or null when the path is absent. */
function readZipText(files: PptxZip, path: string): string | null {
  const bytes = files[path];
  return bytes ? strFromU8(bytes) : null;
}

/** Build the full brand color palette from a BrandSpec. */
function buildColorPalette(brand: BrandSpec): string[] {
  const { colors, fillColorMap, lineColor } = brand;
  return [
    colors.dk1, colors.dk2, colors.lt1, colors.lt2,
    colors.accent1, colors.accent2, colors.accent3,
    colors.accent4, colors.accent5, colors.accent6,
    fillColorMap.primary, fillColorMap.secondary, fillColorMap.tertiary,
    lineColor,
  ].flatMap((c) => {
    const n = normalizeHex(c);
    return n ? [n] : [];
  });
}

/** True when a hex color is acceptably close to any color in the palette. */
function isColorOnBrand(hex: string, palette: string[]): boolean {
  const norm = normalizeHex(hex);
  if (!norm) return true;
  const r = Number.parseInt(norm.slice(0, 2), 16);
  const g = Number.parseInt(norm.slice(2, 4), 16);
  const b = Number.parseInt(norm.slice(4, 6), 16);
  // Near-black and near-white are universally acceptable.
  if (r > 240 && g > 240 && b > 240) return true;
  if (r < 15 && g < 15 && b < 15) return true;
  return palette.some((p) => colorDistance(norm, p) <= COLOR_DISTANCE_THRESHOLD);
}

/**
 * Normalised, de-duplicated list of brand font names (lower-case). Deduping
 * matters because title/body/footnote are frequently the same font — without
 * it, "expected" text built via `.join(" or ")` read as
 * "aptos or aptos or aptos" instead of just "aptos".
 */
function buildBrandFonts(brand: BrandSpec): string[] {
  return [...new Set(
    [brand.fonts.titleFont, brand.fonts.bodyFont, brand.fonts.footnoteFont]
      .filter((f): f is string => Boolean(f))
      .map((f) => f.toLowerCase()),
  )];
}

/** True when the shape's font name matches (or partially matches) a brand font. */
function isFontOnBrand(fontName: string, brandFonts: string[]): boolean {
  if (!fontName || brandFonts.length === 0) return true;
  const lower = fontName.toLowerCase();
  return brandFonts.some((bf) => lower.includes(bf) || bf.includes(lower));
}

let _issueSeq = 0;
function nextId(): string {
  return `bc-${++_issueSeq}`;
}

interface MakeIssueDetails {
  expected?: string;
  actual?: string;
  shapeId?: string;
  fixVia?: NonNullable<BrandIssue["fixVia"]>;
  ooxmlTarget?: NonNullable<BrandIssue["ooxmlTarget"]>;
}

function makeIssue(
  category: BrandIssueCategory,
  slideIndex: number,
  slideNumber: number,
  shapeName: string,
  description: string,
  details: MakeIssueDetails = {},
): BrandIssue {
  const { expected, actual, shapeId, fixVia, ooxmlTarget } = details;
  return {
    id: nextId(), category, slideIndex, slideNumber, shapeName,
    description, expected, actual, shapeId, fixVia, ooxmlTarget,
  };
}

// ─── Per-slide scan context ───────────────────────────────────────────────────

interface SlideScanCtx {
  slideIdx: number;
  slideNumber: number;
  brand: BrandSpec;
  brandFonts: string[];
  colorPalette: string[];
  issues: BrandIssue[];
  /** lower-case shape name → Office.js shape id (top-level + group children) */
  nameToId: Map<string, string>;
  /** dedupe keys: `${shapeNameLower}|${category}` for issues already raised */
  seenKeys: Set<string>;
  /**
   * Shapes whose font.name Office.js resolved (non-null) — brand-checked
   * accurately already, so the OOXML pass must not re-guess them from the
   * theme-inheritance heuristic.
   */
  resolvedFontNames: Set<string>;
  /** Caller-supplied font-size override (from the Font Size Min/Max re-run), or null to use the brand-derived default. */
  fontSizeRange: { min: number; max: number } | null;
  /** Slide canvas size in points, or null when PageSetup (PowerPointApi 1.10) is unavailable in this host — off-slide check is skipped in that case. */
  slideSize: { width: number; height: number } | null;
}

function pushIssue(ctx: SlideScanCtx, issue: BrandIssue): void {
  const key = `${issue.shapeName.toLowerCase()}|${issue.category}`;
  if (ctx.seenKeys.has(key)) return;
  ctx.seenKeys.add(key);
  ctx.issues.push(issue);
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Scan the active presentation against brand and return all detected violations.
 *
 * Discards no state from the caller — every call is a fresh scan. Previous
 * results are discarded by the hook/UI that calls this function.
 *
 * If an error occurs while scanning one slide, scanning continues on the
 * remaining slides — partial results are always returned rather than the
 * whole scan aborting.
 */
export async function checkBrandCompliance(
  brand: BrandSpec,
  options: BrandCheckOptions = {},
): Promise<BrandCheckResult> {
  if (!hasPpt()) {
    throw new Error(
      "PowerPoint host unavailable — open this add-in inside PowerPoint.",
    );
  }

  const { onProgress, fontSizeMin, fontSizeMax } = options;
  const issues: BrandIssue[] = [];
  const colorPalette = buildColorPalette(brand);
  const brandFonts = buildBrandFonts(brand);
  const fontSizeRange = fontSizeMin != null && fontSizeMax != null ? { min: fontSizeMin, max: fontSizeMax } : null;
  let totalSlides = 0;

  try {
    await PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load("items/id");
      await context.sync();

      totalSlides = slides.items.length;
      onProgress?.("Initialising brand check…", 0, totalSlides);
      const slideSize = await resolveSlideSize(context);

      for (let idx = 0; idx < slides.items.length; idx++) {
        const slide = slides.items[idx]!;
        const slideNumber = idx + 1;
        onProgress?.(`Scanning slide ${slideNumber} of ${totalSlides}…`, idx, totalSlides);

        const ctx: SlideScanCtx = {
          slideIdx: idx, slideNumber, brand, brandFonts, colorPalette,
          issues, nameToId: new Map(), seenKeys: new Set(),
          resolvedFontNames: new Set(), fontSizeRange, slideSize,
        };

        try {
          await scanSlide(context, slide, ctx);
        } catch (e) {
          // Skip this slide and keep scanning the rest of the deck.
          logOfficeError(e, "brand-check-slide");
        }

        // OOXML pass — tables, SmartArt, charts, mixed/inherited fonts.
        // Separate try so an Office.js failure never suppresses it (and vice versa).
        try {
          await scanSlideOoxml(context, slide, ctx);
        } catch (e) {
          logOfficeError(e, "brand-check-slide-ooxml");
        }
      }

      onProgress?.("Brand check complete", totalSlides, totalSlides);
    });
  } catch (e) {
    const msg = errorMessage(e, "Brand check failed");
    reportBrandCheckError("brand-check-scan", "BrandCheckError", msg);
    throw e instanceof Error ? e : new Error(msg);
  }

  return { scannedAt: Date.now(), totalSlides, issues };
}

// ─── Office.js slide scanner ──────────────────────────────────────────────────

interface ShapeScanEntry {
  shape: PowerPoint.Shape;
  type: string;
  wantFill: boolean;
  wantText: boolean;
  wantLine: boolean;
  /** left/top/width/height — PowerPointApi 1.4, essentially always available. */
  wantLayout: boolean;
  fillType: string | null;
  hasText: boolean;
  text: string;
  left: number;
  top: number;
  width: number;
  height: number;
  /** Defaults true — a host that can't supply this (pre-1.10) must never manufacture a false "hidden" flag. */
  visible: boolean;
  /** `null` = empty placeholder, a ShapeType string = has content, `undefined` = not a placeholder or unreadable in this host. */
  placeholderContainedType: string | null | undefined;
}

function buildEntry(shape: PowerPoint.Shape): ShapeScanEntry {
  const t = String(shape.type ?? "");
  const isGroup = GROUP_TYPES.has(t);
  return {
    shape,
    type: t,
    wantFill: !NO_FILL_TYPES.has(t) && !isGroup,
    wantText: !NO_TEXTFRAME_TYPES.has(t) && !SPECIAL_TEXT_TYPES.has(t) && !isGroup,
    // Lines/connectors keep their line check — their colour IS the line.
    wantLine: !NO_LINE_TYPES.has(t),
    wantLayout: !isGroup,
    fillType: null,
    hasText: false,
    text: "",
    left: 0, top: 0, width: 0, height: 0,
    visible: true,
    placeholderContainedType: undefined,
  };
}

/** Queue the phase-3 property loads for one entry (each guarded). */
function queueBasicLoads(e: ShapeScanEntry): void {
  if (e.wantFill) {
    try { e.shape.fill.load("type"); } catch (err) {
      e.wantFill = false;
      logOfficeError(err, "brand-check-shape-fill-load");
    }
  }
  if (e.wantText) {
    try { e.shape.textFrame.load("hasText"); } catch (err) {
      e.wantText = false;
      logOfficeError(err, "brand-check-shape-textframe-load");
    }
  }
  if (e.wantLine) {
    try { e.shape.lineFormat.load("color"); } catch (err) {
      e.wantLine = false;
      logOfficeError(err, "brand-check-shape-lineformat-load");
    }
  }
}

/** Queue the phase-4 conditional loads for one entry (each guarded). */
function queueDetailLoads(e: ShapeScanEntry): void {
  if (e.wantFill && e.fillType === "solid") {
    try { e.shape.fill.load("foregroundColor"); } catch (err) {
      logOfficeError(err, "brand-check-shape-fill-foreground-load");
    }
  }
  if (e.wantText && e.hasText) {
    try { e.shape.textFrame.textRange.font.load("name,size,color,bold,italic"); } catch (err) {
      logOfficeError(err, "brand-check-shape-font-load");
    }
    try { e.shape.textFrame.textRange.load("text"); } catch (err) {
      logOfficeError(err, "brand-check-shape-text-load");
    }
  }
}

/**
 * Sync a batch of queued loads; when the batch is poisoned by one invalid
 * load, retry per shape with isolated syncs so the rest of the slide still
 * gets checked (a failed sync discards the whole queued batch).
 */
async function syncWithIsolation(
  context: PowerPoint.RequestContext,
  entries: ShapeScanEntry[],
  queue: (e: ShapeScanEntry) => void,
): Promise<void> {
  try {
    await context.sync();
    return;
  } catch (e) {
    // One poisoned load can fail the whole batch sync — worth tracing since
    // it degrades every shape on the slide to the slower per-shape retry below.
    logOfficeError(e, "brand-check-batch-sync");
  }
  for (const entry of entries) {
    queue(entry);
    try {
      await context.sync();
    } catch (err) {
      entry.wantFill = false;
      entry.wantText = false;
      entry.wantLine = false;
      logOfficeError(err, "brand-check-shape-isolated-sync");
    }
  }
}

/** Phase 1-2 — load the shape collection plus id/name/type, seeding ctx.nameToId. */
async function loadSlideEntries(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide,
  ctx: SlideScanCtx,
): Promise<ShapeScanEntry[]> {
  const shapes = slide.shapes;
  shapes.load("items");
  await context.sync();
  if (shapes.items.length === 0) return [];

  for (const sh of shapes.items) sh.load("id,name,type");
  await context.sync();

  const entries = shapes.items.map(buildEntry);
  for (const e of entries) {
    const nm = String(e.shape.name ?? "").toLowerCase();
    if (nm) ctx.nameToId.set(nm, e.shape.id);
  }
  return entries;
}

function resolveFillType(e: ShapeScanEntry): void {
  if (!e.wantFill) return;
  try {
    e.fillType = String(e.shape.fill.type ?? "").toLowerCase();
  } catch (err) {
    e.wantFill = false;
    logOfficeError(err, "brand-check-shape-fill-type-read");
  }
}

function resolveHasText(e: ShapeScanEntry): void {
  if (!e.wantText) return;
  try {
    e.hasText = e.shape.textFrame.hasText === true;
  } catch (err) {
    e.wantText = false;
    logOfficeError(err, "brand-check-shape-hastext-read");
  }
}

function resolveText(e: ShapeScanEntry): void {
  if (!e.wantText || !e.hasText) return;
  try {
    e.text = e.shape.textFrame.textRange.text ?? "";
  } catch (err) {
    logOfficeError(err, "brand-check-shape-text-read");
  }
}

/**
 * Position (left/top/width/height, PowerPointApi 1.4 — always attempted) in
 * its own isolated sync so a failure here never disturbs the fill/text/line
 * pass, which is a separate, already-hardened batch.
 */
async function loadPositions(context: PowerPoint.RequestContext, entries: ShapeScanEntry[]): Promise<void> {
  for (const e of entries) {
    if (!e.wantLayout) continue;
    try { e.shape.load("left,top,width,height"); } catch (err) {
      e.wantLayout = false;
      logOfficeError(err, "brand-check-shape-position-load");
    }
  }
  try {
    await context.sync();
  } catch (err) {
    for (const e of entries) e.wantLayout = false;
    logOfficeError(err, "brand-check-position-batch-sync");
    return;
  }
  for (const e of entries) {
    if (!e.wantLayout) continue;
    try {
      e.left = e.shape.left; e.top = e.shape.top;
      e.width = e.shape.width; e.height = e.shape.height;
    } catch (err) {
      e.wantLayout = false;
      logOfficeError(err, "brand-check-shape-position-read");
    }
  }
}

/** Queue the phase-5 visibility/placeholder loads for one entry (each guarded). */
function queueVisibilityLoad(e: ShapeScanEntry): void {
  try { e.shape.load("visible"); } catch (err) {
    logOfficeError(err, "brand-check-shape-visible-load");
  }
  if (e.type === "Placeholder") {
    try { e.shape.placeholderFormat.load("containedType"); } catch (err) {
      logOfficeError(err, "brand-check-shape-placeholder-load");
    }
  }
}

/** Read back the phase-5 visibility/placeholder loads for one entry (each guarded). */
function resolveVisibility(e: ShapeScanEntry): void {
  // e.visible keeps its default (true) on failure — never manufactures a false hidden flag.
  try { e.visible = e.shape.visible; } catch (err) {
    logOfficeError(err, "brand-check-shape-visible-read");
  }
  if (e.type === "Placeholder") {
    try { e.placeholderContainedType = e.shape.placeholderFormat.containedType; } catch (err) {
      logOfficeError(err, "brand-check-shape-placeholder-read");
    }
  }
}

/**
 * Visibility + placeholder content (PowerPointApi 1.10 / 1.8 — may be
 * unsupported on older hosts). Kept in its own best-effort sync: a failure
 * here must degrade to "assume visible / not an empty placeholder" rather
 * than ever manufacturing a false Master-Hidden-Object or Empty-Placeholder
 * flag, and must never disturb the fill/text/line/position passes.
 */
async function loadVisibilityAndPlaceholder(
  context: PowerPoint.RequestContext,
  entries: ShapeScanEntry[],
): Promise<void> {
  for (const e of entries) queueVisibilityLoad(e);
  try {
    await context.sync();
  } catch (err) {
    // Whole-slide degrade to "assume visible / not empty" — not a scan-blocking failure.
    logOfficeError(err, "brand-check-visibility-batch-sync");
    return;
  }
  for (const e of entries) resolveVisibility(e);
}

/** Phase 3-4 — batched fill/text/line loads, then conditional detail loads, plus position/visibility passes. */
async function loadShapeProperties(
  context: PowerPoint.RequestContext,
  entries: ShapeScanEntry[],
): Promise<void> {
  for (const e of entries) queueBasicLoads(e);
  await syncWithIsolation(context, entries, queueBasicLoads);

  for (const e of entries) {
    resolveFillType(e);
    resolveHasText(e);
  }

  for (const e of entries) queueDetailLoads(e);
  await syncWithIsolation(context, entries, queueDetailLoads);
  for (const e of entries) resolveText(e);

  await loadPositions(context, entries);
  await loadVisibilityAndPlaceholder(context, entries);
}

function evaluateEntry(e: ShapeScanEntry, ctx: SlideScanCtx): void {
  const shapeName = e.shape.name || `Shape on slide ${ctx.slideNumber}`;
  const shapeId = e.shape.id;

  if (e.wantFill) checkFillColor(e, shapeName, shapeId, ctx);
  if (e.wantLine) checkLineColor(e.shape, shapeName, shapeId, ctx);
  if (e.wantText && e.hasText) {
    checkFontProperties(e.shape, shapeName, shapeId, ctx);
    checkTextQuality(e.text, shapeName, shapeId, ctx);
  }
  if (e.wantLayout) checkOffSlide(e, shapeName, shapeId, ctx);
  checkHiddenObject(e, shapeName, shapeId, ctx);
  checkEmptyPlaceholder(e, shapeName, shapeId, ctx);
}

/** Phase 5 — evaluate every non-group shape against the brand spec. */
function evaluateEntries(entries: ShapeScanEntry[], ctx: SlideScanCtx): void {
  for (const e of entries) {
    if (GROUP_TYPES.has(e.type)) continue; // groups scanned below via recursion
    evaluateEntry(e, ctx);
  }
}

/** Phase 6 — recurse into groups (their children are NOT in slide.shapes). */
async function recurseGroupEntries(
  context: PowerPoint.RequestContext,
  entries: ShapeScanEntry[],
  ctx: SlideScanCtx,
): Promise<void> {
  for (const e of entries) {
    if (!GROUP_TYPES.has(e.type)) continue;
    try {
      await scanGroupChildren(context, e.shape, ctx, 0);
    } catch (err) {
      // The whole group's children are skipped — worth tracing.
      logOfficeError(err, "brand-check-group");
    }
  }
}

async function scanSlide(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide,
  ctx: SlideScanCtx,
): Promise<void> {
  const entries = await loadSlideEntries(context, slide, ctx);
  if (entries.length === 0) return;

  await loadShapeProperties(context, entries);
  evaluateEntries(entries, ctx);
  await recurseGroupEntries(context, entries, ctx);
}

// ─── Group recursion ──────────────────────────────────────────────────────────

async function loadGroupChildFill(
  context: PowerPoint.RequestContext,
  child: PowerPoint.Shape,
  entry: ShapeScanEntry,
): Promise<void> {
  if (!entry.wantFill) return;
  try {
    child.fill.load("type");
    await context.sync();
    entry.fillType = String(child.fill.type ?? "").toLowerCase();
  } catch (err) {
    entry.wantFill = false;
    logOfficeError(err, "brand-check-group-shape-fill-load");
  }

  if (entry.fillType === "solid") {
    try {
      child.fill.load("foregroundColor");
      await context.sync();
    } catch (err) {
      entry.wantFill = false;
      logOfficeError(err, "brand-check-group-shape-fill-foreground-load");
    }
  }
}

async function loadGroupChildText(
  context: PowerPoint.RequestContext,
  child: PowerPoint.Shape,
  entry: ShapeScanEntry,
): Promise<void> {
  if (!entry.wantText) return;
  try {
    child.textFrame.load("hasText");
    await context.sync();
    entry.hasText = child.textFrame.hasText === true;
  } catch (err) {
    entry.wantText = false;
    logOfficeError(err, "brand-check-group-shape-textframe-load");
  }

  if (entry.hasText) {
    try {
      child.textFrame.textRange.font.load("name,size,color,bold,italic");
      await context.sync();
    } catch (err) {
      entry.wantText = false;
      logOfficeError(err, "brand-check-group-shape-font-load");
    }
    try {
      child.textFrame.textRange.load("text");
      await context.sync();
      entry.text = child.textFrame.textRange.text ?? "";
    } catch (err) {
      logOfficeError(err, "brand-check-group-shape-text-load");
    }
  }
}

async function loadGroupChildLine(
  context: PowerPoint.RequestContext,
  child: PowerPoint.Shape,
  entry: ShapeScanEntry,
): Promise<void> {
  if (!entry.wantLine) return;
  try {
    child.lineFormat.load("color");
    await context.sync();
  } catch (err) {
    entry.wantLine = false;
    logOfficeError(err, "brand-check-group-shape-lineformat-load");
  }
}

/** Children are loaded with fully isolated syncs — a bad child never disturbs its siblings. */
async function loadGroupChildDetails(
  context: PowerPoint.RequestContext,
  child: PowerPoint.Shape,
  entry: ShapeScanEntry,
): Promise<void> {
  await loadGroupChildFill(context, child, entry);
  await loadGroupChildText(context, child, entry);
  await loadGroupChildLine(context, child, entry);
}

function evaluateGroupChild(child: PowerPoint.Shape, entry: ShapeScanEntry, ctx: SlideScanCtx): void {
  const shapeName = child.name || `Grouped shape on slide ${ctx.slideNumber}`;
  if (entry.wantFill) checkFillColor(entry, shapeName, child.id, ctx);
  if (entry.wantLine) checkLineColor(child, shapeName, child.id, ctx);
  if (entry.wantText && entry.hasText) {
    checkFontProperties(child, shapeName, child.id, ctx);
    checkTextQuality(entry.text, shapeName, child.id, ctx);
  }
}

async function processGroupChild(
  context: PowerPoint.RequestContext,
  child: PowerPoint.Shape,
  ctx: SlideScanCtx,
  depth: number,
): Promise<void> {
  const childType = String(child.type ?? "");
  const nm = String(child.name ?? "").toLowerCase();
  if (nm) ctx.nameToId.set(nm, child.id);

  if (GROUP_TYPES.has(childType)) {
    try {
      await scanGroupChildren(context, child, ctx, depth + 1);
    } catch (err) {
      // The whole nested group's children are skipped — worth tracing.
      logOfficeError(err, "brand-check-nested-group");
    }
    return;
  }

  const entry = buildEntry(child);
  await loadGroupChildDetails(context, child, entry);
  evaluateGroupChild(child, entry, ctx);
}

async function scanGroupChildren(
  context: PowerPoint.RequestContext,
  groupShape: PowerPoint.Shape,
  ctx: SlideScanCtx,
  depth: number,
): Promise<void> {
  if (depth >= MAX_GROUP_DEPTH) return;

  groupShape.load("group");
  await context.sync();

  const children = groupShape.group.shapes;
  children.load("items");
  await context.sync();
  if (!children.items.length) return;

  for (const child of children.items) child.load("id,name,type");
  await context.sync();

  for (const child of children.items) {
    await processGroupChild(context, child, ctx, depth);
  }
}

// ─── Individual checks ────────────────────────────────────────────────────────

function checkFillColor(
  e: ShapeScanEntry,
  shapeName: string,
  shapeId: string | undefined,
  ctx: SlideScanCtx,
): void {
  try {
    if (e.fillType !== "solid") return;
    const raw = e.shape.fill.foregroundColor;
    if (!raw) return;
    const hex = normalizeHex(raw.replace("#", ""));
    if (hex && !isColorOnBrand(hex, ctx.colorPalette)) {
      pushIssue(ctx, makeIssue(
        "fillColor", ctx.slideIdx, ctx.slideNumber, shapeName,
        "Fill colour does not match the brand palette",
        { expected: "Brand palette colour", actual: `#${hex}`, shapeId },
      ));
    }
  } catch (err) {
    logOfficeError(err, "brand-check-fill-color");
  }
}

function checkLineColor(
  sh: PowerPoint.Shape,
  shapeName: string,
  shapeId: string | undefined,
  ctx: SlideScanCtx,
): void {
  try {
    const raw = sh.lineFormat.color;
    if (!raw) return;
    const hex = normalizeHex(raw.replace("#", ""));
    const brandLineHex = normalizeHex(ctx.brand.lineColor);
    if (!hex) return;
    // Accept if near a brand line color or within the general palette.
    const nearBrandLine =
      brandLineHex && colorDistance(hex, brandLineHex) <= COLOR_DISTANCE_THRESHOLD;
    if (!nearBrandLine && !isColorOnBrand(hex, ctx.colorPalette)) {
      pushIssue(ctx, makeIssue(
        "lineColor", ctx.slideIdx, ctx.slideNumber, shapeName,
        "Line / border colour does not match the brand palette",
        {
          expected: brandLineHex ? `#${brandLineHex}` : "Brand line colour",
          actual: `#${hex}`, shapeId,
        },
      ));
    }
  } catch (err) {
    logOfficeError(err, "brand-check-line-color");
  }
}

/** Font type. Note: font.name is null when the range mixes fonts — that case is caught by the OOXML pass, which sees the underlying runs. */
function checkFontType(
  font: PowerPoint.ShapeFont,
  shapeName: string,
  shapeId: string | undefined,
  ctx: SlideScanCtx,
): void {
  const { brandFonts } = ctx;
  if (font.name) ctx.resolvedFontNames.add(shapeName.toLowerCase());
  if (!font.name || brandFonts.length === 0 || isFontOnBrand(font.name, brandFonts)) return;

  pushIssue(ctx, makeIssue(
    "fontType", ctx.slideIdx, ctx.slideNumber, shapeName,
    `Font "${font.name}" is not a brand-approved font`,
    {
      expected: brandFonts.join(" or "), actual: font.name, shapeId,
      fixVia: "ooxml", ooxmlTarget: "shape",
    },
  ));
}

function checkFontColor(
  font: PowerPoint.ShapeFont,
  shapeName: string,
  shapeId: string | undefined,
  ctx: SlideScanCtx,
): void {
  if (!font.color) return;
  const hex = normalizeHex(font.color.replace("#", ""));
  if (!hex || isColorOnBrand(hex, ctx.colorPalette)) return;

  pushIssue(ctx, makeIssue(
    "fontColor", ctx.slideIdx, ctx.slideNumber, shapeName,
    "Text colour does not match the brand palette",
    { expected: "Brand palette colour", actual: `#${hex}`, shapeId },
  ));
}

/**
 * Default font-size range derived from the brand's title/body sizes (40%
 * smaller to 3× larger). Exported so the UI can seed its Min/Max inputs with
 * real numbers, and so `checkFontSize` and the UI share one formula.
 */
export function computeDefaultFontSizeRange(brand: BrandSpec): { min: number; max: number } | null {
  const bodySize = brand.bodyTextFormat.fontSize;
  const titleSize = brand.titleFormat.fontSize;
  if (bodySize <= 0 || titleSize <= 0) return null;

  return {
    min: Math.round(Math.min(bodySize, titleSize) * 0.6),
    max: Math.round(Math.max(bodySize, titleSize) * 3.0),
  };
}

/** Flags sizes that fall outside the brand's defined range (or the caller's override). */
function checkFontSize(
  font: PowerPoint.ShapeFont,
  shapeName: string,
  shapeId: string | undefined,
  ctx: SlideScanCtx,
): void {
  if (font.size == null) return;
  const range = ctx.fontSizeRange ?? computeDefaultFontSizeRange(ctx.brand);
  if (!range) return;
  const { min: minAllowed, max: maxAllowed } = range;
  if (font.size >= minAllowed && font.size <= maxAllowed) return;

  pushIssue(ctx, makeIssue(
    "fontSize", ctx.slideIdx, ctx.slideNumber, shapeName,
    `Font size ${font.size}pt is outside the brand size range`,
    {
      expected: `${Math.round(minAllowed)}–${Math.round(maxAllowed)}pt`,
      actual: `${font.size}pt`, shapeId,
    },
  ));
}

/** Only flag explicit italic if neither brand font implies italic usage. */
function checkFontItalic(
  font: PowerPoint.ShapeFont,
  shapeName: string,
  shapeId: string | undefined,
  ctx: SlideScanCtx,
): void {
  if (font.italic !== true) return;
  const isItalicExpected = ctx.brandFonts.some((bf) => bf.includes("italic"));
  if (isItalicExpected) return;

  pushIssue(ctx, makeIssue(
    "fontStyle", ctx.slideIdx, ctx.slideNumber, shapeName,
    "Unexpected italic styling — not part of the brand font scheme",
    { expected: "No italic", actual: "Italic", shapeId },
  ));
}

function checkFontProperties(
  sh: PowerPoint.Shape,
  shapeName: string,
  shapeId: string | undefined,
  ctx: SlideScanCtx,
): void {
  try {
    const font = sh.textFrame.textRange.font;
    checkFontType(font, shapeName, shapeId, ctx);
    checkFontColor(font, shapeName, shapeId, ctx);
    checkFontSize(font, shapeName, shapeId, ctx);
    checkFontItalic(font, shapeName, shapeId, ctx);
  } catch (err) {
    logOfficeError(err, "brand-check-font-properties");
  }
}

// ─── Text quality, alignment, and slide-elements hygiene checks ───────────────

const DOUBLE_WORD_RE = /\b(\w+)\s+\1\b/i;
const MULTIPLE_SPACES_RE = / {2,}/;

/** Repeated-word and multiple-consecutive-space hygiene checks over a shape's plain text. */
function checkTextQuality(
  text: string,
  shapeName: string,
  shapeId: string | undefined,
  ctx: SlideScanCtx,
): void {
  if (!text) return;

  const doubleWordMatch = DOUBLE_WORD_RE.exec(text);
  if (doubleWordMatch) {
    pushIssue(ctx, makeIssue(
      "doubleWords", ctx.slideIdx, ctx.slideNumber, shapeName,
      `Repeated word "${doubleWordMatch[1]}"`,
      { actual: doubleWordMatch[0], shapeId },
    ));
  }

  if (MULTIPLE_SPACES_RE.test(text)) {
    pushIssue(ctx, makeIssue(
      "multipleSpaces", ctx.slideIdx, ctx.slideNumber, shapeName,
      "Multiple consecutive spaces",
      { shapeId },
    ));
  }
}

/** Flags shapes whose bounding box falls outside the slide canvas. Scoped to top-level shapes only (group-relative offsets aren't resolved). */
function checkOffSlide(
  e: ShapeScanEntry,
  shapeName: string,
  shapeId: string | undefined,
  ctx: SlideScanCtx,
): void {
  if (!ctx.slideSize) return;
  const { width: slideW, height: slideH } = ctx.slideSize;
  const { left, top, width, height } = e;
  const tol = OFF_SLIDE_TOLERANCE_PT;

  const offLeft = left < -tol;
  const offTop = top < -tol;
  const offRight = left + width > slideW + tol;
  const offBottom = top + height > slideH + tol;
  if (!offLeft && !offTop && !offRight && !offBottom) return;

  pushIssue(ctx, makeIssue(
    "offSlide", ctx.slideIdx, ctx.slideNumber, shapeName,
    "Shape is positioned partly or fully off the visible slide area",
    {
      expected: `Within ${Math.round(slideW)}×${Math.round(slideH)}pt`,
      actual: `left ${Math.round(left)}pt, top ${Math.round(top)}pt`,
      shapeId,
    },
  ));
}

/**
 * Master-Hidden-Objects: a shape left hidden on the slide (visible === false).
 * Auto-fixable via reveal (shape.visible = true) — not delete, since a hidden
 * shape may have been hidden intentionally; surfacing it is non-destructive.
 */
function checkHiddenObject(
  e: ShapeScanEntry,
  shapeName: string,
  shapeId: string | undefined,
  ctx: SlideScanCtx,
): void {
  if (e.visible !== false) return;
  pushIssue(ctx, makeIssue(
    "hiddenObject", ctx.slideIdx, ctx.slideNumber, shapeName,
    "Hidden shape left on the slide",
    { actual: "Hidden", shapeId },
  ));
}

/**
 * Empty-Placeholders: a placeholder shape with no content typed/inserted into it.
 *
 * `placeholderFormat.containedType` is `null` for EVERY plain text placeholder
 * (title/body/subtitle holding typed text directly) — not just genuinely empty
 * ones; it only resolves to something for placeholders holding a distinct
 * nested object (table/chart/picture/SmartArt). Treating a null containedType
 * alone as "empty" flags essentially every title/body box in a normal deck.
 * Requiring `!hasText` too (and skipping when text-loading itself failed, so a
 * load failure never manufactures a false "empty" flag) is the actual "empty"
 * signal. Report-only (see REPORT_ONLY note in brandFixer.ts) — never auto-deletes.
 */
function checkEmptyPlaceholder(
  e: ShapeScanEntry,
  shapeName: string,
  shapeId: string | undefined,
  ctx: SlideScanCtx,
): void {
  if (e.type !== "Placeholder" || e.placeholderContainedType !== null) return;
  if (!e.wantText || e.hasText) return;
  pushIssue(ctx, makeIssue(
    "emptyPlaceholder", ctx.slideIdx, ctx.slideNumber, shapeName,
    "Empty placeholder with no content",
    { actual: "Empty", shapeId },
  ));
}

// ─── OOXML supplemental scan ──────────────────────────────────────────────────
//
// Exports the slide as a pptx fragment and inspects the raw XML. This is the
// only way to check content the Office.js shape API cannot reach:
//   - table cell text and fills
//   - SmartArt (ppt/diagrams/*.xml parts)
//   - charts (ppt/charts/*.xml parts)
//   - text runs with mixed fonts (font.name === null via Office.js)
//   - fonts inherited from the deck theme (+mj-lt / +mn-lt / no explicit latin)

interface ThemeInfo {
  majorFont: string;
  minorFont: string;
  scheme: Record<string, string>;
}

async function loadThemeInfo(files: PptxZip): Promise<ThemeInfo> {
  const info: ThemeInfo = { majorFont: "", minorFont: "", scheme: {} };
  const themePath = Object.keys(files).find((p) => /^ppt\/theme\/theme\d+\.xml$/.test(p));
  if (!themePath) return info;
  const xml = readZipText(files, themePath) ?? "";

  info.majorFont = new RegExp(/<a:majorFont>[\s\S]*?<a:latin\b[^>]*\btypeface="([^"]*)"/).exec(xml)?.[1] ?? "";
  info.minorFont = new RegExp(/<a:minorFont>[\s\S]*?<a:latin\b[^>]*\btypeface="([^"]*)"/).exec(xml)?.[1] ?? "";

  const clrScheme = new RegExp(/<a:clrScheme\b[\s\S]*?<\/a:clrScheme>/).exec(xml)?.[0] ?? "";
  // Slot name is any theme colour tag (dk1, lt1, accent1-6, hlink, folHlink, …) —
  // matched generically since only the srgbClr/sysClr payload after it is checked.
  const slotRe = /<a:(\w+)>\s*(?:<a:srgbClr\s+val="([0-9A-Fa-f]{6})"|<a:sysClr\b[^>]*\blastClr="([0-9A-Fa-f]{6})")/g;
  for (const m of clrScheme.matchAll(slotRe)) {
    const slot = m[1];
    const hex = (m[2] ?? m[3] ?? "").toUpperCase();
    if (slot && hex) info.scheme[slot] = hex;
  }
  // Text/background aliases used inside slides map onto the dk/lt slots.
  if (info.scheme.dk1) info.scheme.tx1 = info.scheme.dk1;
  if (info.scheme.lt1) info.scheme.bg1 = info.scheme.lt1;
  if (info.scheme.dk2) info.scheme.tx2 = info.scheme.dk2;
  if (info.scheme.lt2) info.scheme.bg2 = info.scheme.lt2;
  return info;
}

/** True when the XML block contains at least one non-empty text run. */
function hasVisibleText(xml: string): boolean {
  for (const m of xml.matchAll(/<a:t>([^<]*)<\/a:t>/g)) {
    if (/\S/.test(m[1] ?? "")) return true;
  }
  return false;
}

/** Resolve a <a:latin> typeface attribute to its real font name (+mj-lt / +mn-lt theme refs expanded). */
function resolveLatinFace(rawFace: string, theme: ThemeInfo): string {
  if (rawFace === "+mj-lt") return theme.majorFont;
  if (rawFace === "+mn-lt") return theme.minorFont;
  if (rawFace.startsWith("+")) return "";
  return rawFace;
}

/** Off-brand explicit <a:latin> faces in the block; returns whether any explicit face was seen at all. */
function collectExplicitOffBrandFonts(
  xml: string,
  brandFonts: string[],
  theme: ThemeInfo,
  offBrand: Set<string>,
): boolean {
  let sawExplicit = false;
  for (const m of xml.matchAll(/<a:latin\b[^>]*\btypeface="([^"]*)"/g)) {
    const face = resolveLatinFace(m[1] ?? "", theme);
    if (!face) continue;
    sawExplicit = true;
    if (SYMBOL_FONTS.has(face.toLowerCase())) continue;
    if (!isFontOnBrand(face, brandFonts)) offBrand.add(face);
  }
  return sawExplicit;
}

/** When text has no explicit latin face at all, it inherits the theme font — check that instead. */
function collectInheritedOffBrandFont(
  xml: string,
  brandFonts: string[],
  theme: ThemeInfo,
  isTitle: boolean,
  offBrand: Set<string>,
): void {
  if (!hasVisibleText(xml)) return;
  const inherited = isTitle ? (theme.majorFont || theme.minorFont) : theme.minorFont;
  if (inherited && !isFontOnBrand(inherited, brandFonts)) offBrand.add(inherited);
}

/**
 * Collect off-brand font faces used in an XML block. Resolves +mj-lt / +mn-lt
 * theme references; when the block has text but NO explicit latin faces at
 * all, the text inherits the theme font — which is then checked instead.
 */
function collectOffBrandFonts(
  xml: string,
  brandFonts: string[],
  theme: ThemeInfo,
  isTitle: boolean,
  skipInherited = false,
): string[] {
  const offBrand = new Set<string>();
  const sawExplicit = collectExplicitOffBrandFonts(xml, brandFonts, theme, offBrand);

  if (!sawExplicit && !skipInherited) {
    collectInheritedOffBrandFont(xml, brandFonts, theme, isTitle, offBrand);
  }

  return [...offBrand];
}

/** Collect off-brand solid-fill colours (explicit + theme-resolved) in a block. */
function collectOffBrandFills(
  xml: string,
  colorPalette: string[],
  theme: ThemeInfo,
): string[] {
  const offBrand = new Set<string>();
  for (const m of xml.matchAll(/<a:solidFill>\s*<a:srgbClr\s+val="([0-9A-Fa-f]{6})"/g)) {
    const hex = (m[1] ?? "").toUpperCase();
    if (hex && !isColorOnBrand(hex, colorPalette)) offBrand.add(`#${hex}`);
  }
  for (const m of xml.matchAll(/<a:solidFill>\s*<a:schemeClr\s+val="(\w+)"/g)) {
    const hex = theme.scheme[m[1] ?? ""];
    if (hex && !isColorOnBrand(hex, colorPalette)) offBrand.add(`#${hex}`);
  }
  return [...offBrand];
}

function extractShapeName(block: string): string {
  return new RegExp(/<p:cNvPr\b[^>]*\bname="([^"]*)"/).exec(block)?.[1] ?? "";
}

/** Shared context threaded through the per-section OOXML scanners below. */
interface OoxmlScanCtx {
  ctx: SlideScanCtx;
  theme: ThemeInfo;
  brandFonts: string[];
  colorPalette: string[];
  expectedFonts: string;
}

/** Tables (graphicFrame blocks containing <a:tbl>). */
function scanTablesOoxml(frames: string[], octx: OoxmlScanCtx): void {
  const { ctx, theme, brandFonts, colorPalette, expectedFonts } = octx;
  for (const frame of frames) {
    if (!frame.includes("<a:tbl")) continue;
    const name = extractShapeName(frame) || "Table";
    const shapeId = ctx.nameToId.get(name.toLowerCase());

    const badFonts = collectOffBrandFonts(frame, brandFonts, theme, false);
    if (badFonts.length > 0) {
      pushIssue(ctx, makeIssue(
        "fontType", ctx.slideIdx, ctx.slideNumber, name,
        `Table text uses non-brand font${badFonts.length > 1 ? "s" : ""}: ${badFonts.join(", ")}`,
        { expected: expectedFonts, actual: badFonts.join(", "), shapeId, fixVia: "ooxml", ooxmlTarget: "table" },
      ));
    }
    const badFills = collectOffBrandFills(frame, colorPalette, theme);
    if (badFills.length > 0) {
      pushIssue(ctx, makeIssue(
        "fillColor", ctx.slideIdx, ctx.slideNumber, name,
        "Table colours do not match the brand palette",
        { expected: "Brand palette colour", actual: badFills.join(", "), shapeId, fixVia: "ooxml", ooxmlTarget: "table" },
      ));
    }
  }
}

/** SmartArt (diagram parts). */
function scanSmartArtOoxml(files: PptxZip, frames: string[], octx: OoxmlScanCtx): void {
  const { ctx, theme, brandFonts, colorPalette, expectedFonts } = octx;
  const diagramPaths = Object.keys(files).filter((p) => /^ppt\/diagrams\/[^/]+\.xml$/.test(p));
  if (diagramPaths.length === 0) return;

  const diagramFrame = frames.find(
    (f) => f.includes("<dgm:relIds") || f.includes("drawingml/2006/diagram"),
  );
  const name = (diagramFrame ? extractShapeName(diagramFrame) : "") || "SmartArt";
  const shapeId = ctx.nameToId.get(name.toLowerCase());

  const badFonts = new Set<string>();
  const badFills = new Set<string>();
  for (const path of diagramPaths) {
    const dxml = readZipText(files, path) ?? "";
    collectOffBrandFonts(dxml, brandFonts, theme, false).forEach((f) => badFonts.add(f));
    collectOffBrandFills(dxml, colorPalette, theme).forEach((f) => badFills.add(f));
  }
  if (badFonts.size > 0) {
    pushIssue(ctx, makeIssue(
      "fontType", ctx.slideIdx, ctx.slideNumber, name,
      `SmartArt text uses non-brand font${badFonts.size > 1 ? "s" : ""}: ${[...badFonts].join(", ")}`,
      { expected: expectedFonts, actual: [...badFonts].join(", "), shapeId, fixVia: "ooxml", ooxmlTarget: "diagram" },
    ));
  }
  if (badFills.size > 0) {
    pushIssue(ctx, makeIssue(
      "fillColor", ctx.slideIdx, ctx.slideNumber, name,
      "SmartArt colours do not match the brand palette",
      { expected: "Brand palette colour", actual: [...badFills].join(", "), shapeId, fixVia: "ooxml", ooxmlTarget: "diagram" },
    ));
  }
}

/** Charts (chart parts). */
function scanChartsOoxml(files: PptxZip, frames: string[], octx: OoxmlScanCtx): void {
  const { ctx, theme, brandFonts, expectedFonts } = octx;
  const chartPaths = Object.keys(files).filter((p) => /^ppt\/charts\/chart\d+\.xml$/.test(p));
  if (chartPaths.length === 0) return;

  const chartFrame = frames.find((f) => f.includes("drawingml/2006/chart"));
  const name = (chartFrame ? extractShapeName(chartFrame) : "") || "Chart";
  const shapeId = ctx.nameToId.get(name.toLowerCase());

  const badFonts = new Set<string>();
  for (const path of chartPaths) {
    const cxml = readZipText(files, path) ?? "";
    collectOffBrandFonts(cxml, brandFonts, theme, false).forEach((f) => badFonts.add(f));
  }
  if (badFonts.size > 0) {
    pushIssue(ctx, makeIssue(
      "fontType", ctx.slideIdx, ctx.slideNumber, name,
      `Chart text uses non-brand font${badFonts.size > 1 ? "s" : ""}: ${[...badFonts].join(", ")}`,
      { expected: expectedFonts, actual: [...badFonts].join(", "), shapeId, fixVia: "ooxml", ooxmlTarget: "chart" },
    ));
  }
}

/**
 * Leaf <p:sp> shape — mixed or theme-inherited fonts. Office.js font.name
 * returns null on mixed-font ranges, so those shapes were invisible to the
 * shape scan. The pushIssue dedupe key means shapes already flagged by the
 * Office.js pass are not double-reported.
 */
function scanShapeOoxml(sp: string, octx: OoxmlScanCtx): void {
  const { ctx, theme, brandFonts, expectedFonts } = octx;
  if (!hasVisibleText(sp)) return;
  const name = extractShapeName(sp);
  if (!name) return;

  const isTitle = /<p:ph\b[^>]*\btype="(?:title|ctrTitle)"/.test(sp);
  // When Office.js resolved this shape's font, it was already checked
  // accurately — only the explicit run faces still need inspecting here.
  const skipInherited = ctx.resolvedFontNames.has(name.toLowerCase());
  const badFonts = collectOffBrandFonts(sp, brandFonts, theme, isTitle, skipInherited);
  if (badFonts.length === 0) return;

  const shapeId = ctx.nameToId.get(name.toLowerCase());
  pushIssue(ctx, makeIssue(
    "fontType", ctx.slideIdx, ctx.slideNumber, name,
    `Font "${badFonts.join(", ")}" is not a brand-approved font`,
    {
      expected: expectedFonts, actual: badFonts.join(", "), shapeId,
      fixVia: "ooxml", ooxmlTarget: "shape",
    },
  ));
}

function scanShapesOoxml(xml: string, octx: OoxmlScanCtx): void {
  const spBlocks = xml.match(/<p:sp\b[\s\S]*?<\/p:sp>/g) ?? [];
  for (const sp of spBlocks) scanShapeOoxml(sp, octx);
}

// ─── Bullet colour, alignment, and slide-elements hygiene (OOXML) ─────────────
//
// Office.js's BulletFormat exposes only style/type/visible — no colour — so
// bullet colour can only be checked via the raw <a:buClr> markup. Notes,
// animations, transitions and comments have no Office.js surface at all;
// they're read from the same per-slide export already fetched above.

const BULLET_COLOR_RE = /<a:buClr>\s*<a:srgbClr\s+val="([0-9A-Fa-f]{6})"/g;

/** Explicit off-brand <a:buClr> colours per shape. Paragraphs with no explicit colour (inherited) are skipped — no false positives. */
function scanBulletColorsOoxml(xml: string, octx: OoxmlScanCtx): void {
  const { ctx, colorPalette } = octx;
  const spBlocks = xml.match(/<p:sp\b[\s\S]*?<\/p:sp>/g) ?? [];
  for (const sp of spBlocks) {
    const name = extractShapeName(sp);
    if (!name) continue;

    const badColors = new Set<string>();
    for (const m of sp.matchAll(BULLET_COLOR_RE)) {
      const hex = (m[1] ?? "").toUpperCase();
      if (hex && !isColorOnBrand(hex, colorPalette)) badColors.add(`#${hex}`);
    }
    if (badColors.size === 0) continue;

    const shapeId = ctx.nameToId.get(name.toLowerCase());
    pushIssue(ctx, makeIssue(
      "bulletColor", ctx.slideIdx, ctx.slideNumber, name,
      "Bullet colour does not match the brand palette",
      {
        expected: "Brand palette colour", actual: [...badColors].join(", "), shapeId,
        fixVia: "ooxml", ooxmlTarget: "shape",
      },
    ));
  }
}

const TRANSITION_RE = /<p:transition\b/;

/**
 * Transitions-Present: a <p:transition> element on the slide. Auto-fixable —
 * removing a transition effect deletes no content, only a visual behavior
 * (unlike Notes/Comments/Empty-Placeholders, where "fixing" would mean
 * deleting real authored content).
 */
function scanTransitionOoxml(xml: string, ctx: SlideScanCtx): void {
  if (!TRANSITION_RE.test(xml)) return;
  pushIssue(ctx, makeIssue(
    "transitionsPresent", ctx.slideIdx, ctx.slideNumber, "Slide Transition",
    "Slide has a transition effect applied",
    { fixVia: "ooxml", ooxmlTarget: "slide" },
  ));
}

const TIMING_RE = /<p:timing>[\s\S]*?<\/p:timing>/;
const ANIMATION_EFFECT_RE = /<p:(?:anim\b|animClr\b|animRot\b|animScale\b|animEffect\b|set\b|audio\b|video\b)/;

/** Animations-Present: <p:timing> containing an actual effect node (an empty/no-op timing tree doesn't count). Auto-fixable — see scanTransitionOoxml. */
function scanAnimationsOoxml(xml: string, ctx: SlideScanCtx): void {
  const timing = TIMING_RE.exec(xml)?.[0];
  if (!timing || !ANIMATION_EFFECT_RE.test(timing)) return;
  pushIssue(ctx, makeIssue(
    "animationsPresent", ctx.slideIdx, ctx.slideNumber, "Slide Animation",
    "Slide has animation effects applied",
    { fixVia: "ooxml", ooxmlTarget: "slide" },
  ));
}

/**
 * Notes-Present: real (non-auto-placeholder) speaker notes text exists for
 * this slide. Auto-fixable — "Fix"/"Fix All" clears the notes text entirely
 * (see brandFixer.ts's "notes" ooxmlTarget), per explicit product decision.
 * This IS destructive (deletes authored notes content) — unlike the other
 * Slide Elements categories, which stay report-only.
 */
function scanNotesOoxml(files: PptxZip, ctx: SlideScanCtx): void {
  if (!extractNotesText(files).trim()) return;
  pushIssue(ctx, makeIssue(
    "notesPresent", ctx.slideIdx, ctx.slideNumber, "Speaker Notes",
    "Slide has speaker notes",
    { fixVia: "ooxml", ooxmlTarget: "notes" },
  ));
}

/** Comments-Present: the slide has a comments-relationship part with at least one non-empty comment. Report-only — never auto-deletes comments. */
function scanCommentsOoxml(files: PptxZip, ctx: SlideScanCtx): void {
  const relsXml = readZipText(files, SLIDE1_RELS_PATH);
  const commentsPath = resolveRelationshipTarget(relsXml, isCommentsRelationship, SLIDE_PART_DIR);
  if (!commentsPath) return;
  const commentsXml = readZipText(files, commentsPath);
  if (!commentsXml) return;

  const hasComment = [...commentsXml.matchAll(/<[\w.]*:text\b[^>]*>([^<]*)<\/[\w.]*:text>/gi)]
    .some((m) => Boolean(m[1]?.trim()));
  if (!hasComment) return;

  pushIssue(ctx, makeIssue(
    "commentsPresent", ctx.slideIdx, ctx.slideNumber, "Slide Comments",
    "Slide has review comments",
  ));
}

async function scanSlideOoxml(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide,
  ctx: SlideScanCtx,
): Promise<void> {
  let base64: string;
  try {
    const exportResult = slide.exportAsBase64();
    await context.sync();
    base64 = exportResult.value;
  } catch (err) {
    // exportAsBase64 unavailable in this host — Office.js results only.
    logOfficeError(err, "brand-check-ooxml-export-unavailable");
    return;
  }

  let files: PptxZip;
  try {
    files = await unzipPptxBase64(base64);
  } catch (err) {
    // Export succeeded but the returned pptx was unreadable — a genuine,
    // lower-frequency failure worth tracing.
    logOfficeError(err, "brand-check-ooxml-unzip");
    return;
  }

  const xml = readZipText(files, SLIDE1_XML_PATH);
  if (xml === null) return;
  const theme = await loadThemeInfo(files);
  const { brandFonts, colorPalette } = ctx;
  const octx: OoxmlScanCtx = { ctx, theme, brandFonts, colorPalette, expectedFonts: brandFonts.join(" or ") };

  const frames = xml.match(/<p:graphicFrame\b[\s\S]*?<\/p:graphicFrame>/g) ?? [];
  scanTablesOoxml(frames, octx);
  scanSmartArtOoxml(files, frames, octx);
  scanChartsOoxml(files, frames, octx);
  scanShapesOoxml(xml, octx);
  scanBulletColorsOoxml(xml, octx);
  scanTransitionOoxml(xml, ctx);
  scanAnimationsOoxml(xml, ctx);
  scanNotesOoxml(files, ctx);
  scanCommentsOoxml(files, ctx);
}
